import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { User, CheckCircle2, Clock, PlayCircle, StopCircle, Timer, Pause, Snowflake, Hash, Pencil, Calculator, Save, X, Zap } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function ConclusaoCard({ conclusao, categoriasTarefas, onUpdated }) {
  const catInfo = categoriasTarefas.find(c => c.id === conclusao.tarefa_categoria_id);
  const [editingQty, setEditingQty] = useState(false);
  const [novaQty, setNovaQty] = useState(conclusao.quantidade ?? 0);
  const [savingQty, setSavingQty] = useState(false);

  const formatTime = (isoStr, withSeconds = false) => {
    if (!isoStr) return null;
    const d = new Date(isoStr);
    return d.toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit', ...(withSeconds ? { second: '2-digit' } : {}) });
  };

  const formatDate = (isoStr) => {
    if (!isoStr) return null;
    return new Date(isoStr).toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit', year: '2-digit' });
  };

  const calcDuracao = () => {
    let segs = null;
    if (conclusao.tempo_segundos !== undefined && conclusao.tempo_segundos !== null) {
      segs = Number(conclusao.tempo_segundos);
    } else if (conclusao.tempo_minutos !== undefined && conclusao.tempo_minutos !== null) {
      segs = Math.round(Number(conclusao.tempo_minutos) * 60);
    } else if (conclusao.inicio_em && conclusao.concluido_em) {
      segs = Math.max(0, Math.floor((new Date(conclusao.concluido_em) - new Date(conclusao.inicio_em)) / 1000));
    }
    if (segs === null || isNaN(segs)) return null;
    if (segs < 60) return `${segs}s`;
    const mins = Math.floor(segs / 60);
    const restSegs = segs % 60;
    if (mins < 60) return restSegs > 0 ? `${mins}m ${restSegs}s` : `${mins}m`;
    const hrs = Math.floor(mins / 60);
    const restMins = mins % 60;
    return restMins > 0 ? `${hrs}h ${restMins}m` : `${hrs}h`;
  };

  const inicioTime = formatTime(conclusao.inicio_em, true);
  const fimTime = formatTime(conclusao.concluido_em, true);
  const dataConclusao = formatDate(conclusao.concluido_em);
  const duracao = calcDuracao();
  const qtd = conclusao.quantidade ?? 0;
  const foiPausada = !!(conclusao.foi_pausada || (conclusao.contador_pausas || 0) > 0 || (conclusao.total_pausa_segundos || 0) > 0);
  const foiCongelada = !!(conclusao.foi_congelada || (conclusao.contador_congelacoes || 0) > 0);
  const isContagem = !!(conclusao.obriga_contagem || conclusao.is_contagem || (Array.isArray(conclusao.operacoes) && conclusao.operacoes.some(o => o?.is_contagem === true)));
  const isUrgente = !!conclusao.urgente;
  const qtdInicial = conclusao.quantidade_inicial;
  const foiEditada = qtdInicial !== undefined && qtdInicial !== null && qtdInicial !== qtd;
  const editadoEm = conclusao.editado_em;

  const guardarQty = async () => {
    if (savingQty) return;
    const v = parseInt(novaQty, 10);
    if (isNaN(v) || v < 0) return;
    setSavingQty(true);
    try {
      await axios.patch(`${API}/tarefas/conclusoes/${conclusao.id}/quantidade`, {
        quantidade: v,
        editado_por_nome: 'Gestor',
      });
      setEditingQty(false);
      if (onUpdated) onUpdated();
    } catch (e) {
      // Manter aberto em erro e informar o utilizador
      toast.error('Erro ao guardar a quantidade: ' + (e.response?.data?.detail || e.message));
    } finally { setSavingQty(false); }
  };

  return (
    <div className={`bg-white rounded-lg border p-2.5 transition-all hover:shadow-sm ${isUrgente ? 'border-l-4 border-l-red-500 border-blue-100' : 'border-blue-100'}`} data-testid={`conclusao-card-${conclusao.id}`}>
      <div className="flex items-start justify-between gap-2 mb-1">
        <h4 className="font-semibold text-sm text-navy truncate flex-1 min-w-0">
          {conclusao.tarefa_nome}
        </h4>
        {catInfo && (
          <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium flex-shrink-0" style={{ backgroundColor: `${catInfo.cor}20`, color: catInfo.cor }}>
            {catInfo.nome}
          </span>
        )}
      </div>

      {/* Linha de badges (Urgente + CONT). Coexistem sempre — wrap se necessário. */}
      {(isUrgente || isContagem) && (
        <div className="flex flex-wrap items-center gap-1 mb-1.5" data-testid={`conclusao-badges-${conclusao.id}`}>
          {isUrgente && (
            <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded font-bold bg-red-100 text-red-700" title="Tarefa urgente" data-testid={`badge-urgente-${conclusao.id}`}>
              <Zap className="h-2.5 w-2.5" />Urgente
            </span>
          )}
          {isContagem && (
            <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded font-bold bg-blue-100 text-blue-700" title="Tarefa com contagem" data-testid={`badge-cont-${conclusao.id}`}>
              <Calculator className="h-2.5 w-2.5" />CONT
            </span>
          )}
        </div>
      )}
      {conclusao.operador_nome && (
        <div className="flex items-center gap-1 text-xs text-blue-600 mb-1">
          <User className="h-3 w-3" /><span>{conclusao.operador_nome}</span>
        </div>
      )}

      <div className="flex items-center gap-2 mb-1.5">
        {!editingQty ? (
          <>
            <div className="flex items-center gap-1.5 bg-emerald-50 text-emerald-700 rounded-md px-2 py-1 flex-1">
              <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
              <span className="font-extrabold text-base leading-none">{qtd}</span>
              <span className="text-xs font-semibold opacity-80">{qtd === 1 ? 'peça' : 'peças'}</span>
              <button onClick={() => { setNovaQty(qtd); setEditingQty(true); }}
                className="ml-auto p-0.5 rounded hover:bg-emerald-200 text-emerald-700"
                title="Editar quantidade" data-testid={`edit-qtd-${conclusao.id}`}>
                <Pencil className="h-3 w-3" />
              </button>
            </div>
            {foiPausada && (
              <span className="text-[10px] px-1.5 py-0.5 rounded font-bold bg-amber-100 text-amber-700 flex items-center gap-0.5" title={`Pausada${conclusao.contador_pausas ? ' ('+conclusao.contador_pausas+'x)' : ''}`}>
                <Pause className="h-2.5 w-2.5" />{conclusao.contador_pausas ? `${conclusao.contador_pausas}x` : 'Pausada'}
              </span>
            )}
            {foiCongelada && (
              <span className="text-[10px] px-1.5 py-0.5 rounded font-bold bg-cyan-100 text-cyan-700 flex items-center gap-0.5" title={`Congelada${conclusao.contador_congelacoes ? ' ('+conclusao.contador_congelacoes+'x)' : ''}`}>
                <Snowflake className="h-2.5 w-2.5" />{conclusao.contador_congelacoes ? `${conclusao.contador_congelacoes}x` : 'Congelada'}
              </span>
            )}
          </>
        ) : (
          <div className="flex items-center gap-1 flex-1 bg-emerald-50 rounded-md px-2 py-1">
            <CheckCircle2 className="h-4 w-4 text-emerald-700 flex-shrink-0" />
            <input type="number" min="0" value={novaQty}
              onChange={(e) => setNovaQty(e.target.value)}
              autoFocus
              className="flex-1 bg-white border border-emerald-300 rounded px-2 py-0.5 text-sm font-bold text-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-400"
              data-testid={`input-qtd-${conclusao.id}`}
            />
            <button onClick={guardarQty} disabled={savingQty}
              className="p-1 rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
              title="Guardar" data-testid={`save-qtd-${conclusao.id}`}>
              <Save className="h-3 w-3" />
            </button>
            <button onClick={() => setEditingQty(false)}
              className="p-1 rounded text-stone-500 hover:bg-stone-100" title="Cancelar">
              <X className="h-3 w-3" />
            </button>
          </div>
        )}
      </div>

      {foiEditada && !editingQty && (
        <div className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 mb-1 flex items-center gap-1">
          <Pencil className="h-2.5 w-2.5" />
          <span>Editada — original: <strong>{qtdInicial}</strong> {editadoEm && <>· {formatDate(editadoEm)} {formatTime(editadoEm)}</>}</span>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[10px] text-stone-500 mb-1">
        <span className="flex items-center gap-0.5" title="Início">
          <PlayCircle className="h-2.5 w-2.5 text-green-500" />
          <span className={inicioTime ? 'text-stone-600 font-medium' : 'italic text-stone-400'}>{inicioTime || '—'}</span>
        </span>
        <span className="text-stone-300">→</span>
        <span className="flex items-center gap-0.5" title="Fim">
          <StopCircle className="h-2.5 w-2.5 text-red-400" />
          <span className={fimTime ? 'text-stone-600 font-medium' : 'italic text-stone-400'}>{fimTime || '—'}</span>
        </span>
        {duracao && (
          <span className="flex items-center gap-0.5 font-bold text-blue-600 ml-auto" title="Duração total">
            <Timer className="h-2.5 w-2.5" />{duracao}
          </span>
        )}
      </div>

      {conclusao.observacoes && (
        <div className="text-[11px] text-stone-500 italic line-clamp-1 mb-1">"{conclusao.observacoes}"</div>
      )}
      {dataConclusao && (
        <div className="flex items-center justify-between gap-1 text-[10px] text-stone-400">
          <span className="flex items-center gap-1"><Clock className="h-2.5 w-2.5" />{dataConclusao}</span>
          {conclusao.id && (
            <span className="flex items-center gap-0.5 font-mono opacity-60" title={`ID: ${conclusao.id}`}>
              <Hash className="h-2 w-2" />{String(conclusao.id).slice(0, 6)}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
